import isEmpty from 'lodash/isEmpty';
import {
	get as getAvatars,
	save as saveAvatars,
} from '@atlassian/jira-common-rest/src/api/2/universal-avatar/type/issuetype/owner/id/rest.tsx';
import type { CroppingProperties } from '@atlassian/jira-common-rest/src/api/2/universal-avatar/type/issuetype/owner/id/types.tsx';
import { PACKAGE_NAME } from '../consts.tsx';
import { toErrorId, toErrorMessage, type Avatar } from '../types.tsx';
import { famaCatchAndLogFetchError } from '../util/catch-and-log-fetch-error.tsx';
import { logError } from '../util/error-logger.tsx';
import normalizeAvatars, { normaliseSavedAvatar } from './avatar-normalizer.tsx';
import avatarStorage from './avatar-storage.tsx';

export type AvatarService = {
	get: (projectId: number) => Promise<Avatar[]>;
	save: (projectId: number, image: File, properties: CroppingProperties) => Promise<Avatar>;
};

type Get = (projectId: number) => Promise<Avatar[]>;

export const get: Get = (projectId: number): Promise<Avatar[]> => {
	const storedAvatars: Avatar[] = avatarStorage.get(projectId);

	if (!isEmpty(storedAvatars)) {
		return Promise.resolve(storedAvatars);
	}

	const getAvatarsPromise = famaCatchAndLogFetchError(
		getAvatars(projectId),
		PACKAGE_NAME,
		toErrorId('getIssueTypeAvatars'),
		toErrorMessage('Failed to fetch issue type avatars for a project'),
	);

	return getAvatarsPromise.then((rawAvatars) => {
		try {
			const avatars = normalizeAvatars(rawAvatars);
			avatarStorage.init(projectId, avatars);
			return avatars;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			logError(
				PACKAGE_NAME,
				toErrorId('processIssueTypeAvatars'),
				toErrorMessage('Failed to normalize issue type avatars or initialize avatar storage'),
				error,
			);

			throw error;
		}
	});
};

type Save = (projectId: number, arg2: File, arg3: CroppingProperties) => Promise<Avatar>;

const save: Save = (projectId: number, image: File, properties: CroppingProperties) => {
	const saveAvatarPromise = famaCatchAndLogFetchError(
		saveAvatars(projectId, image, properties),
		PACKAGE_NAME,
		toErrorId('uploadIssueTypeAvatar'),
		toErrorMessage('Failed to upload an issue type avatar for a project'),
	);

	return saveAvatarPromise.then((result) => {
		try {
			const normalizedAvatar = normaliseSavedAvatar(result);
			avatarStorage.update(projectId, normalizedAvatar);
			return normalizedAvatar;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			logError(
				PACKAGE_NAME,
				toErrorId('processUploadedIssueTypeAvatar'),
				toErrorMessage(
					'Failed to normalize an uploaded issue type avatar or update avatar storage',
				),
				error,
			);

			throw error;
		}
	});
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default {
	get,
	save,
};
